import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CANCEL_BTN_TEXT,
  RESET_CONFIRM_BTN,
  RESET_FULL_MODE_ENABLED,
  RESET_MODE_DATA_ONLY,
  RESET_MODE_DATA_ONLY_HINT,
  RESET_MODE_DATA_ONLY_LABEL,
  RESET_MODE_FULL,
  RESET_MODE_FULL_HINT,
  RESET_MODE_FULL_LABEL,
  RESET_MODE_FULL_UNAVAILABLE,
  RESET_MODE_LABEL,
  RESET_NAME_MISMATCH,
  RESET_COMPANY_DATA_TITLE,
  RESET_WARNING_DATA_ONLY_KEEPS,
  RESET_WARNING_ACTIVITY,
  RESET_WARNING_DATA_ONLY_REMOVES,
  RESET_WARNING_FULL,
  RESET_WARNING_HEADLINE,
} from '../../constants';

export interface ResetCompanyDataDialogData {
  companyName: string;
}

/** What the dialog closes with when the admin confirms; `undefined` when cancelled/closed. */
export interface ResetCompanyDataDialogResult {
  mode: typeof RESET_MODE_DATA_ONLY | typeof RESET_MODE_FULL;
  confirmName: string;
}

/**
 * SYRC-7421: confirmation dialog for "Reset Company Data" (data-only company wipe).
 *  - mode choice (Data only is the default; Full is shown disabled until SYRC-7543 sets
 *    RESET_FULL_MODE_ENABLED);
 *  - an unmistakable irreversible warning that lists what is removed and what is kept;
 *  - the admin must type the exact company name before the (red) confirm button enables.
 * The server re-validates the mode and the name; this dialog only gates the UI.
 */
@Component({
  selector: 'app-reset-company-data-dialog',
  templateUrl: './reset-company-data-dialog.component.html',
  styleUrl: './reset-company-data-dialog.component.css',
})
export class ResetCompanyDataDialogComponent {
  readonly modeLabel = RESET_MODE_LABEL;
  readonly dataOnlyMode = RESET_MODE_DATA_ONLY;
  readonly fullMode = RESET_MODE_FULL;
  readonly dataOnlyLabel = RESET_MODE_DATA_ONLY_LABEL;
  readonly dataOnlyHint = RESET_MODE_DATA_ONLY_HINT;
  readonly fullLabel = RESET_MODE_FULL_LABEL;
  readonly fullHint = RESET_MODE_FULL_HINT;
  readonly fullUnavailable = RESET_MODE_FULL_UNAVAILABLE;
  readonly fullEnabled = RESET_FULL_MODE_ENABLED;
  readonly warningHeadline = RESET_WARNING_HEADLINE;
  readonly dataOnlyRemoves = RESET_WARNING_DATA_ONLY_REMOVES;
  readonly dataOnlyKeeps = RESET_WARNING_DATA_ONLY_KEEPS;
  readonly activityWarning = RESET_WARNING_ACTIVITY;
  readonly fullWarning = RESET_WARNING_FULL;
  readonly confirmLabel = RESET_CONFIRM_BTN;
  readonly cancelLabel = CANCEL_BTN_TEXT;

  selectedMode: string = RESET_MODE_DATA_ONLY;
  typedName = '';

  constructor(
    private dialogRef: MatDialogRef<
      ResetCompanyDataDialogComponent,
      ResetCompanyDataDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: ResetCompanyDataDialogData,
  ) {}

  get title(): string {
    return RESET_COMPANY_DATA_TITLE(this.data.companyName);
  }

  /**
   * The typed name must match the company name exactly. Surrounding whitespace is ignored on BOTH sides,
   * the same rule the server applies, so a company whose stored name has stray spaces can still be confirmed.
   */
  get nameMatches(): boolean {
    return this.typedName.trim() === (this.data.companyName ?? '').trim();
  }

  /** Mismatch hint only once something is typed — an empty field is not an error. */
  get showMismatchError(): boolean {
    return this.typedName.trim().length > 0 && !this.nameMatches;
  }

  get mismatchMessage(): string {
    return RESET_NAME_MISMATCH(this.data.companyName);
  }

  get isConfirmDisabled(): boolean {
    return !this.nameMatches || !this.isSelectedModeAvailable;
  }

  private get isSelectedModeAvailable(): boolean {
    return (
      this.selectedMode === RESET_MODE_DATA_ONLY ||
      (this.selectedMode === RESET_MODE_FULL && RESET_FULL_MODE_ENABLED)
    );
  }

  onNameInput(event: Event): void {
    this.typedName = (event.target as HTMLInputElement).value;
  }

  selectMode(mode: string): void {
    if (mode === RESET_MODE_FULL && !RESET_FULL_MODE_ENABLED) {
      return;
    }
    this.selectedMode = mode;
  }

  onConfirm(): void {
    if (this.isConfirmDisabled) {
      return;
    }
    this.dialogRef.close({
      mode: this.selectedMode as ResetCompanyDataDialogResult['mode'],
      confirmName: this.typedName.trim(),
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
